import { Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { useCommandPaletteStore } from "@/stores/commandPaletteStore";
import { useEffect, Suspense, lazy } from "react";
import { Loader2 } from "lucide-react";

// Layout
import { DashboardLayout } from "@/components/DashboardLayout";

// Error boundary and command palette
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CommandPalette } from "@/components/CommandPalette";

// Capacitor push notification handler (BP-074: rendered inside Router)
import { PushNotificationHandler } from "@/components/PushNotificationHandler";

// Lazy-loaded pages for code splitting
const Login = lazy(() =>
  import("@/pages/Login").then((m) => ({ default: m.Login })),
);
const Positions = lazy(() =>
  import("@/pages/Positions").then((m) => ({ default: m.Positions })),
);
const Performance = lazy(() =>
  import("@/pages/Performance").then((m) => ({ default: m.Performance })),
);
const Scanner = lazy(() =>
  import("@/pages/Scanner").then((m) => ({ default: m.Scanner })),
);
const TradeTracker = lazy(() =>
  import("@/pages/TradeTracker").then((m) => ({ default: m.TradeTracker })),
);
const Chat = lazy(() =>
  import("@/pages/Chat").then((m) => ({ default: m.Chat })),
);
const Leaderboard = lazy(() =>
  import("@/pages/Leaderboard").then((m) => ({ default: m.Leaderboard })),
);
const Calendar = lazy(() =>
  import("@/pages/Calendar").then((m) => ({ default: m.Calendar })),
);
const Feedback = lazy(() =>
  import("@/pages/Feedback").then((m) => ({ default: m.Feedback })),
);
const Help = lazy(() =>
  import("@/pages/Help").then((m) => ({ default: m.Help })),
);
const PostMortems = lazy(() =>
  import("@/pages/PostMortems").then((m) => ({ default: m.PostMortems })),
);
const PromptVersions = lazy(() =>
  import("@/pages/PromptVersions").then((m) => ({ default: m.PromptVersions })),
);
const ShadowBook = lazy(() =>
  import("@/pages/ShadowBook").then((m) => ({ default: m.ShadowBook })),
);
const UnusualActivity = lazy(() =>
  import("@/pages/UnusualActivity").then((m) => ({
    default: m.UnusualActivity,
  })),
);
const Polymarket = lazy(() =>
  import("@/pages/Polymarket").then((m) => ({ default: m.Polymarket })),
);
const CatalystTracker = lazy(() =>
  import("@/pages/CatalystTracker").then((m) => ({
    default: m.CatalystTracker,
  })),
);
const TailRisk = lazy(() =>
  import("@/pages/TailRisk").then((m) => ({ default: m.TailRisk })),
);
const EarningsAlpha = lazy(() =>
  import("@/pages/EarningsAlpha").then((m) => ({ default: m.EarningsAlpha })),
);
const ResearchWorkflow = lazy(() => import("@/pages/ResearchWorkflow"));
const Valuations = lazy(() =>
  import("@/pages/Valuations").then((m) => ({ default: m.Valuations })),
);
const Backtester = lazy(() => import("@/pages/Backtester"));
const AutonomousPipeline = lazy(() => import("@/pages/AutonomousPipeline"));
const TradeDoctor = lazy(() => import("@/pages/TradeDoctor"));
const SlippageAnalyzer = lazy(() => import("@/pages/SlippageAnalyzer"));
const AlphaAttribution = lazy(() => import("@/pages/AlphaAttribution"));
const FinTwit = lazy(() => import("@/pages/fintwit"));
const PerformanceAttribution = lazy(
  () => import("@/pages/PerformanceAttribution"),
);
const RiskPreferences = lazy(() =>
  import("@/pages/RiskPreferences").then((m) => ({
    default: m.RiskPreferences,
  })),
);
const TaxSummary = lazy(() =>
  import("@/pages/TaxSummary").then((m) => ({ default: m.TaxSummary })),
);
const PortfolioOptimizer = lazy(() => import("@/pages/PortfolioOptimizer"));
const Triage = lazy(() =>
  import("@/pages/Triage").then((m) => ({ default: m.Triage })),
);
const RiskPolicies = lazy(() =>
  import("@/pages/RiskPolicies").then((m) => ({ default: m.RiskPolicies })),
);
const DevStudio = lazy(() =>
  import("@/pages/DevValue").then((m) => ({ default: m.DevValue })),
);
const BrokerSync = lazy(() => import("@/pages/BrokerSync"));
const Accounts = lazy(() => import("@/pages/Accounts"));
const WeeklyScorecard = lazy(() => import("@/pages/WeeklyScorecard"));
const RollBarbell = lazy(() => import("@/pages/RollBarbell"));
const RiskPackPage = lazy(() =>
  import("@/pages/RiskPack/RiskPackPage").then((m) => ({
    default: m.RiskPackPage,
  })),
);
const Flow = lazy(() => import("@/pages/Flow/FlowPage"));
const Signals = lazy(() => import("@/pages/Signals/SignalsPage"));
const ModelHealth = lazy(() => import("@/pages/ModelHealth"));
const TradeChains = lazy(() => import("@/pages/TradeChains"));

// Loading spinner for lazy-loaded pages
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-full min-h-[400px]">
      <Loader2 className="size-8 animate-spin text-primary" />
    </div>
  );
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { status, checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Show loading while checking auth
  if (status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full size-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (status !== "authenticated") {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  // Command palette state (shared via Zustand store for mobile access)
  const commandPaletteOpen = useCommandPaletteStore((s) => s.open);
  const setCommandPaletteOpen = useCommandPaletteStore((s) => s.setOpen);

  // Global keyboard shortcut for command palette
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        useCommandPaletteStore.getState().toggle();
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  return (
    <ErrorBoundary>
      {/* Global command palette */}
      <CommandPalette
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
      />

      {/* Capacitor push notification handler — inside Routes for useNavigate() */}
      <PushNotificationHandler />

      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={<Navigate to="/research-workflow" replace />}
            />
            <Route path="positions" element={<Positions />} />
            <Route path="scanner" element={<Scanner />} />
            <Route path="performance" element={<Performance />} />
            <Route path="trade-tracker" element={<TradeTracker />} />
            <Route path="chat" element={<Chat />} />
            {/* Legacy routes redirect to research-workflow */}
            <Route
              path="trade-generator"
              element={<Navigate to="/research-workflow" replace />}
            />
            <Route
              path="research"
              element={<Navigate to="/research-workflow" replace />}
            />
            <Route path="leaderboard" element={<Leaderboard />} />
            <Route path="post-mortems" element={<PostMortems />} />
            <Route path="calendar" element={<Calendar />} />
            <Route path="feedback" element={<Feedback />} />
            <Route path="help" element={<Help />} />
            <Route path="prompt-lab" element={<PromptVersions />} />
            <Route path="shadow-book" element={<ShadowBook />} />
            <Route path="unusual-activity" element={<UnusualActivity />} />
            <Route path="polymarket" element={<Polymarket />} />
            <Route path="catalysts" element={<CatalystTracker />} />
            <Route path="tail-risk" element={<TailRisk />} />
            <Route path="earnings-alpha" element={<EarningsAlpha />} />
            <Route path="research-workflow" element={<ResearchWorkflow />} />
            <Route path="valuations" element={<Valuations />} />
            <Route path="backtester" element={<Backtester />} />
            <Route path="pipeline" element={<AutonomousPipeline />} />
            <Route path="trade-doctor" element={<TradeDoctor />} />
            <Route path="slippage" element={<SlippageAnalyzer />} />
            <Route path="alpha-attribution" element={<AlphaAttribution />} />
            <Route path="fintwit-alpha" element={<FinTwit />} />
            <Route
              path="performance-attribution"
              element={<PerformanceAttribution />}
            />
            <Route path="risk-preferences" element={<RiskPreferences />} />
            <Route path="tax-summary" element={<TaxSummary />} />
            <Route
              path="portfolio-optimizer"
              element={<PortfolioOptimizer />}
            />
            <Route path="triage" element={<Triage />} />
            <Route path="risk-policies" element={<RiskPolicies />} />
            <Route path="dev-studio" element={<DevStudio />} />
            <Route
              path="dev-value"
              element={<Navigate to="/dev-studio" replace />}
            />
            <Route path="broker-sync" element={<BrokerSync />} />
            <Route path="accounts" element={<Accounts />} />
            <Route path="weekly-scorecard" element={<WeeklyScorecard />} />
            <Route path="roll-barbell" element={<RollBarbell />} />
            <Route path="flow" element={<Flow />} />
            <Route path="signals" element={<Signals />} />
            <Route path="model-health" element={<ModelHealth />} />
            <Route path="trade-chains" element={<TradeChains />} />
            <Route
              path="risk-pack"
              element={
                <ErrorBoundary
                  fallback={
                    <div className="p-6 text-center text-negative">
                      <p className="font-semibold">
                        Risk pack failed to render. Try refreshing.
                      </p>
                    </div>
                  }
                >
                  <RiskPackPage />
                </ErrorBoundary>
              }
            />
          </Route>

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
